import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../../utils/idbUtils";

const DB_NAME = "grpc_monitoreo_usuarios";
const DB_VERSION = 3;
const STORE_USUARIOS = "usuarios";
const STORE_SYNC = "sync";

/** Item mínimo que necesita el merge de delta (mismo contrato que
 *  `GrpcTareaItem`: watermark por cliente, no uno global). */
export type GrpcMonitoreoUsuarioItem = {
  id: string;
  eliminado: boolean;
  ultimaActualizacion: bigint;
};

/** IndexedDB **nueva y separada** para los usuarios de la app de monitoreo que
 *  trae `MonitoreoUsuarioGrpcService` (ver CLAUDE.md del Server, sección
 *  migración a gRPC-Web) — mismo patrón que `GrpcTareaDatabase`: delta real
 *  por `clienteId`. */
class GrpcMonitoreoUsuarioDatabase {
  /** Conexión a la base local con el ayudante común, que al cambiar de
   *  versión conserva los datos (ver `fx_crearBaseLocal`). La versión 3
   *  (2026-09-30) se subió a pedido del usuario para una limpieza única al
   *  estrenar el ayudante: un navegador con una versión anterior vacía la
   *  base una sola vez y la vuelve a llenar desde el servidor. */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_USUARIOS, clave: "id", indices: ["clienteId"] },
      { nombre: STORE_SYNC, clave: "clienteId" },
    ],
    { vaciarAntesDe: 3 },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  async getUltimaSincronizacionAsync(clienteId: string): Promise<bigint> {
    const db = await this.open();
    const store = db.transaction(STORE_SYNC, "readonly").objectStore(STORE_SYNC);
    const row = await reqToPromise<{ clienteId: string; ultimaSincronizacion: string } | undefined>(
      store.get(clienteId),
    );
    return row ? BigInt(row.ultimaSincronizacion) : 0n;
  }

  async putDeltaAsync<T extends GrpcMonitoreoUsuarioItem>(
    clienteId: string,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([STORE_USUARIOS, STORE_SYNC], "readwrite");
    const store = tx.objectStore(STORE_USUARIOS);
    const syncStore = tx.objectStore(STORE_SYNC);

    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    syncStore.put({ clienteId, ultimaSincronizacion: nuevoWatermark.toString() });

    await txDone(tx);
  }

  async getListByClienteIdAsync<T>(clienteId: string): Promise<T[]> {
    const db = await this.open();
    const index = db
      .transaction(STORE_USUARIOS, "readonly")
      .objectStore(STORE_USUARIOS)
      .index("clienteId");
    return reqToPromise<T[]>(index.getAll(clienteId));
  }

  /** Actualiza (o inserta) un solo usuario sin tocar el resto (alta,
   *  activar/inactivar). */
  async putOneAsync<T extends GrpcMonitoreoUsuarioItem>(item: T): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_USUARIOS, "readwrite");
    tx.objectStore(STORE_USUARIOS).put(item);
    await txDone(tx);
  }
}

/** Singleton de la base de usuarios de monitoreo por gRPC-Web (con delta por
 *  cliente). */
export const grpcMonitoreoUsuarioDatabase = new GrpcMonitoreoUsuarioDatabase();
